import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import MomentDatePicker from 'src/mentor/components/PrescriptionMakingPage/inputComponents/diagnosis/components/MomentDatePicker'
import {
  EDVCDateContainer,
  DateNavButton,
  TodayButton,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'

dayjs.extend(customParseFormat)

const FMT = 'DD-MM-YYYY'
const todayStr = () => dayjs().format(FMT)

/**
 * Human "how far from today" label, in the largest sensible unit. Returns null for today.
 * e.g. { text: '3 days back', ahead: false } / { text: '2 months ahead', ahead: true }
 */
function relativeToToday(target) {
  const today = dayjs().startOf('day')
  const day = target.startOf('day')
  const diffDays = day.diff(today, 'day')
  if (diffDays === 0) return null
  const ahead = diffDays > 0
  const absYears = Math.abs(day.diff(today, 'year'))
  const absMonths = Math.abs(day.diff(today, 'month'))
  const absDays = Math.abs(diffDays)
  let value
  let unit
  if (absYears >= 1) {
    value = absYears
    unit = 'year'
  } else if (absMonths >= 1) {
    value = absMonths
    unit = 'month'
  } else {
    value = absDays
    unit = 'day'
  }
  return { text: `${value} ${unit}${value === 1 ? '' : 's'} ${ahead ? 'ahead' : 'back'}`, ahead }
}

/**
 * Sleek day navigator for the main page header (mentor clinic bookings).
 *
 * It stays in sync with the calendar WITHOUT lifting state, using the event bridge that
 * DayViewCalendarComponent already exposes: that component dispatches 'calendar-internal-date-change'
 * whenever its date changes, and listens for 'calendar-date-change' to be driven from outside. So
 * this navigator just mirrors the former and emits the latter.
 *
 * Rendered only when the page is wide enough; otherwise the calendar's own floating header keeps
 * showing the date picker (handled by the caller via a width check).
 */
export default function BookingDateNavigator({ clinicSchedules }) {
  const [dateStr, setDateStr] = useState(todayStr())

  useEffect(() => {
    const onInternal = (e) => {
      const d = e?.detail?.date
      if (d) setDateStr((prev) => (d !== prev ? d : prev))
    }
    window.addEventListener('calendar-internal-date-change', onInternal)
    return () => window.removeEventListener('calendar-internal-date-change', onInternal)
  }, [])

  const apply = (next) => {
    if (!next) return
    setDateStr(next)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('calendar-date-change', { detail: { date: next } }))
    }
  }

  const d = dayjs(dateStr, FMT)
  const isToday = dateStr === todayStr()
  const offset = relativeToToday(d)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
    <EDVCDateContainer
      compact
      style={{ display: 'flex', flexWrap: 'nowrap', width: 'auto', alignItems: 'center', gap: 6 }}
    >
      <DateNavButton
        onClick={() => apply(d.subtract(1, 'day').format(FMT))}
        title="Previous day"
        style={{ flexShrink: 0 }}
      >
        <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
      </DateNavButton>

      <MomentDatePicker
        date={d}
        buttonTextOverride={`${d.format('ddd, D MMM')}${isToday ? ' · Today' : ''}`}
        buttonProps={{
          variant: 'outlined',
          size: 'small',
          endIcon: <CalendarMonthRoundedIcon />,
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            color: 'rgba(67, 56, 202, 0.9)',
            textTransform: 'none',
            border: '1px solid rgba(67, 56, 202, 0.25)',
            fontWeight: 700,
            fontSize: '13px',
            padding: '6px 12px',
            borderRadius: '10px',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            boxShadow: '0 1px 2px rgba(67, 56, 202, 0.05)',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'rgba(67, 56, 202, 0.45)',
              background: 'rgba(67, 56, 202, 0.08)',
              color: 'rgba(67, 56, 202, 1)',
            },
          },
        }}
        onChange={(nextValue) => apply(nextValue?.format(FMT))}
        calendarProps={{
          minDetail: 'decade',
          showNeighboringMonth: false,
          locale: (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('languageCode')) || 'en',
        }}
        clinicSchedules={clinicSchedules}
        selectedDate={dateStr}
      />

      {!isToday && (
        <TodayButton onClick={() => apply(todayStr())} title="Jump to today" style={{ flexShrink: 0 }}>
          <TodayRoundedIcon sx={{ fontSize: 18 }} />
        </TodayButton>
      )}

      <DateNavButton
        onClick={() => apply(d.add(1, 'day').format(FMT))}
        title="Next day"
        style={{ flexShrink: 0 }}
      >
        <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
      </DateNavButton>
    </EDVCDateContainer>

    {/* How far the chosen day is from today — colour-coded so past vs future reads at a glance. */}
    {offset && (
      <Box
        component="span"
        title={offset.ahead ? 'This date is in the future' : 'This date is in the past'}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          fontSize: '10px',
          fontWeight: 700,
          lineHeight: 1,
          padding: '2px 8px',
          borderRadius: '999px',
          whiteSpace: 'nowrap',
          letterSpacing: '0.2px',
          color: offset.ahead ? '#1d4ed8' : '#b45309',
          backgroundColor: offset.ahead ? 'rgba(59, 130, 246, 0.14)' : 'rgba(245, 158, 11, 0.18)',
          border: `1px solid ${offset.ahead ? 'rgba(59, 130, 246, 0.3)' : 'rgba(245, 158, 11, 0.35)'}`,
        }}
      >
        {offset.ahead ? (
          <ArrowForwardRoundedIcon sx={{ fontSize: 12 }} />
        ) : (
          <ArrowBackRoundedIcon sx={{ fontSize: 12 }} />
        )}
        {offset.text}
      </Box>
    )}
    </Box>
  )
}
